import { useEffect, useState } from 'react';
import axios from 'axios';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { History } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  created: 'created the view',
  edited: 'edited',
  pinned: 'pinned to Dashboard',
  unpinned: 'unpinned from Dashboard',
  schedule_changed: 'changed schedule',
  slug_set: 'set permalink',
};

const labelFor = (entry) => {
  const action = ACTION_LABELS[entry.action] || entry.action;
  if (entry.action === 'edited' && entry.meta?.fields) {
    return `${action} (${entry.meta.fields.join(', ')})`;
  }
  if (entry.action === 'schedule_changed' && entry.meta) {
    return entry.meta.enabled
      ? `enabled ${entry.meta.frequency || ''} email schedule`
      : 'cleared email schedule';
  }
  if (entry.action === 'slug_set' && entry.meta?.slug) {
    return `set permalink /v/${entry.meta.slug}`;
  }
  return action;
};

/**
 * Activity-log viewer for a saved view. Shows the last 50 audit-log entries
 * with relative timestamps. Read-only.
 */
const ViewActivityLogDialog = ({ view, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!view) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API_URL}/api/saved-views/${view.id}/audit`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setEntries(Array.isArray(data) ? [...data].reverse() : []);
      } catch {
        if (!cancelled) setEntries([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [view]);

  return (
    <Dialog open={Boolean(view)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="view-activity-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-[#111565]" />
            Activity for “{view?.name}”
          </DialogTitle>
          <DialogDescription>Most recent first; up to 50 entries.</DialogDescription>
        </DialogHeader>
        <div className="py-2 max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">
              No activity recorded yet.
            </p>
          ) : (
            <ol className="space-y-1.5" data-testid="activity-log-list">
              {entries.map((e, idx) => {
                let when = '';
                try { when = formatDistanceToNow(parseISO(e.ts), { addSuffix: true }); } catch { /* noop */ }
                return (
                  <li
                    key={`${e.ts}-${idx}`}
                    className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-xs"
                    data-testid={`activity-entry-${idx}`}
                  >
                    <span className="inline-block mt-1 w-1.5 h-1.5 rounded-full bg-[#111565] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">
                        <strong className="text-foreground">{e.user_name || 'Someone'}</strong>{' '}
                        <span className="text-muted-foreground">{labelFor(e)}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">{when}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewActivityLogDialog;
